import { ClearRounded, DeleteOutlineRounded, FileDownloadRounded, RefreshRounded, UploadRounded } from "@mui/icons-material";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	Typography,
} from "@mui/material";
import ExcelJS from "exceljs";
import TanstackDatagrid from "components/ReusableComponents/TanstackDatagrid/TanstackDatagrid";
import axios from "axios";
import { useMemo, useState } from "react";

const RR_CODE_HEADERS = ["rr_code", "i_code", "icode", "rrcode"];
const GINESYS_CODE_HEADERS = ["ginesys_code", "ginesyscode"];

const normalizeHeader = (value) =>
	String(value ?? "")
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "_")
		.replace(/^_+|_+$/g, "");

const normalizeKeyValue = (value) => String(value ?? "").trim().toUpperCase();

const parseCsvLine = (line) => {
	const values = [];
	let current = "";
	let inQuotes = false;

	for (let index = 0; index < line.length; index += 1) {
		const char = line[index];
		const nextChar = line[index + 1];

		if (char === '"') {
			if (inQuotes && nextChar === '"') {
				current += '"';
				index += 1;
			} else {
				inQuotes = !inQuotes;
			}
			continue;
		}

		if (char === "," && !inQuotes) {
			values.push(current.trim());
			current = "";
			continue;
		}

		current += char;
	}

	values.push(current.trim());
	return values;
};

const formatCellValue = (value) => {
	if (value == null) return "";
	if (typeof value === "object") {
		if (value?.text != null) return String(value.text).trim();
		if (value?.result != null) return String(value.result).trim();
		if (value?.richText?.length)
			return value.richText
				.map((part) => part.text)
				.join("")
				.trim();
	}
	return String(value).trim();
};

const statusColor = (row) => {
	if (row.__status === "Will replace") return "warning.main";
	if (row.__status === "Unchanged") return "text.secondary";
	if (row.__isValid === false) return "error.main";
	return "success.main";
};

const GinesysCodeUploadModal = ({ modal, setModal, callbackFun }) => {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [notice, setNotice] = useState("");
	const [fileName, setFileName] = useState("");
	const [headers, setHeaders] = useState([]);
	const [sourceRows, setSourceRows] = useState([]);
	const [rrColumnIndex, setRrColumnIndex] = useState("");
	const [ginesysColumnIndex, setGinesysColumnIndex] = useState("");
	const [previewRows, setPreviewRows] = useState([]);

	const resetState = () => {
		setLoading(false);
		setError("");
		setNotice("");
		setFileName("");
		setHeaders([]);
		setSourceRows([]);
		setRrColumnIndex("");
		setGinesysColumnIndex("");
		setPreviewRows([]);
	};

	const handleClose = () => {
		resetState();
		setModal(false);
	};

	const getDefaultIndex = (list, targetKeys) => list.findIndex((header) => targetKeys.includes(normalizeHeader(header)));

	const buildMappedRows = (rows = sourceRows, rrColumn = rrColumnIndex, ginesysColumn = ginesysColumnIndex) => {
		if (rrColumn === "" || ginesysColumn === "") {
			throw new Error("Select RR Code and Ginesys Code columns first.");
		}
		if (rrColumn === ginesysColumn) {
			throw new Error("RR Code and Ginesys Code must be mapped to different columns.");
		}

		const rrIndex = Number(rrColumn);
		const ginesysIndex = Number(ginesysColumn);
		const groupedRows = rows.reduce((acc, row, index) => {
			const rrCode = formatCellValue(row?.[rrIndex]);
			const ginesysCode = formatCellValue(row?.[ginesysIndex]);
			if (!rrCode) return acc;

			const key = normalizeKeyValue(rrCode);
			if (!acc[key]) {
				acc[key] = {
					__rowId: `ginesys-upload-row-${index}`,
					i_code: rrCode,
					ginesys_code: ginesysCode,
					__status: "Pending",
					__note: "",
					__count: 0,
					__codes: new Set(),
				};
			}
			acc[key].__count += 1;
			acc[key].__codes.add(ginesysCode);
			return acc;
		}, {});

		return Object.values(groupedRows).map((row) => {
			if (!row.ginesys_code) {
				return { ...row, __isValid: false, __status: "Invalid", __note: "Missing Ginesys code" };
			}
			if (row.__codes.size > 1) {
				return {
					...row,
					__isValid: false,
					__status: "Duplicate",
					__note: "RR code has multiple different Ginesys codes in the file",
				};
			}
			return {
				...row,
				__isValid: true,
				__status: "Valid",
				__note: row.__count > 1 ? `Merged ${row.__count} rows with the same Ginesys code` : "Ready to upload",
			};
		});
	};

	const annotateExistingCodes = async (rows) => {
		const codes = rows.filter((row) => row.__isValid !== false).map((row) => row.i_code);
		if (!codes.length) return rows;

		const response = await axios.post("/api/studio/preview_ginesys_code_upload", { i_codes: codes });
		const lookup = new Map((response.data?.result || []).map((row) => [normalizeKeyValue(row.i_code), row]));

		return rows.map((row) => {
			if (row.__isValid === false) return row;
			const match = lookup.get(normalizeKeyValue(row.i_code));
			if (!match?.exists) {
				return { ...row, __isValid: false, __status: "Missing", __note: "RR code not found" };
			}
			const currentCode = `${match.ginesys_code || ""}`.trim();
			const nextCode = `${row.ginesys_code || ""}`.trim();
			if (currentCode && currentCode !== nextCode) {
				return {
					...row,
					__isValid: true,
					__status: "Will replace",
					__note: `${currentCode} -> ${nextCode}`,
				};
			}
			if (currentCode && currentCode === nextCode) {
				return { ...row, __isValid: true, __status: "Unchanged", __note: `Already ${currentCode}` };
			}
			return { ...row, __isValid: true, __status: "Valid", __note: row.__note || "Ready to upload" };
		});
	};

	const previewMappedRows = async (rows, rrColumn, ginesysColumn) => {
		const mappedRows = buildMappedRows(rows, rrColumn, ginesysColumn);
		return annotateExistingCodes(mappedRows);
	};

	const parseCsvFile = async (file) => {
		const text = await file.text();
		const lines = text.split(/\r?\n/).filter((line) => line.trim());
		if (!lines.length) return { headers: [], rows: [] };

		const parsedHeaders = parseCsvLine(lines[0]).map((value, index) => value || `Column ${index + 1}`);
		const parsedRows = lines.slice(1).map((line) => parseCsvLine(line));
		return { headers: parsedHeaders, rows: parsedRows };
	};

	const parseExcelFile = async (file) => {
		const workbook = new ExcelJS.Workbook();
		const buffer = await file.arrayBuffer();
		await workbook.xlsx.load(buffer);
		const worksheet = workbook.worksheets[0];
		if (!worksheet) return { headers: [], rows: [] };

		const parsedHeaders = worksheet
			.getRow(1)
			.values.slice(1)
			.map((value, index) => formatCellValue(value) || `Column ${index + 1}`);
		const parsedRows = [];
		worksheet.eachRow((row, rowNumber) => {
			if (rowNumber === 1) return;
			parsedRows.push(row.values.slice(1));
		});
		return { headers: parsedHeaders, rows: parsedRows };
	};

	const handleFileChange = async (event) => {
		const selectedFile = event.target.files?.[0];
		event.target.value = "";
		if (!selectedFile) return;

		setLoading(true);
		setError("");
		setNotice("");
		try {
			const isCsv = selectedFile.name.toLowerCase().endsWith(".csv");
			const parsedData = isCsv ? await parseCsvFile(selectedFile) : await parseExcelFile(selectedFile);
			const rrIndex = getDefaultIndex(parsedData.headers, RR_CODE_HEADERS);
			const ginesysIndex = getDefaultIndex(parsedData.headers, GINESYS_CODE_HEADERS);
			const nextRr = rrIndex >= 0 ? String(rrIndex) : "";
			const nextGinesys = ginesysIndex >= 0 ? String(ginesysIndex) : "";

			setFileName(selectedFile.name);
			setHeaders(parsedData.headers);
			setSourceRows(parsedData.rows);
			setRrColumnIndex(nextRr);
			setGinesysColumnIndex(nextGinesys);

			if (nextRr !== "" && nextGinesys !== "") {
				setPreviewRows(await previewMappedRows(parsedData.rows, nextRr, nextGinesys));
			} else {
				setPreviewRows([]);
			}
		} catch (uploadError) {
			resetState();
			setError(uploadError?.response?.data?.message || uploadError.message || "Unable to read the selected file.");
		} finally {
			setLoading(false);
		}
	};

	const handlePreview = async () => {
		setLoading(true);
		setError("");
		setNotice("");
		try {
			setPreviewRows(await previewMappedRows(sourceRows));
		} catch (previewError) {
			setPreviewRows([]);
			setError(previewError?.response?.data?.message || previewError.message || "Unable to preview file.");
		} finally {
			setLoading(false);
		}
	};

	const handleDownloadFormat = async () => {
		const workbook = new ExcelJS.Workbook();
		const worksheet = workbook.addWorksheet("Ginesys Code Upload");
		worksheet.addRow(["RR Code", "Ginesys Code"]);
		worksheet.addRow(["RR1234", "GIN001"]);

		const buffer = await workbook.xlsx.writeBuffer();
		const blob = new Blob([buffer], {
			type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		});
		const link = document.createElement("a");
		link.href = URL.createObjectURL(blob);
		link.download = "ginesys_code_upload_format.xlsx";
		link.click();
		URL.revokeObjectURL(link.href);
	};

	const handleDeleteRow = (rowId) => {
		setPreviewRows((prev) => prev.filter((row) => row.__rowId !== rowId));
	};

	const uploadableRows = useMemo(
		() => previewRows.filter((row) => row.__isValid !== false && row.__status !== "Unchanged"),
		[previewRows],
	);

	const handleFormSubmit = async (event) => {
		event.preventDefault();
		if (typeof callbackFun !== "function") return;
		if (previewRows.some((row) => row.__isValid === false && row.__status === "Duplicate")) {
			setError("Each RR code must have the same Ginesys code across all rows.");
			return;
		}
		if (!uploadableRows.length) {
			setError("No Ginesys codes to upload.");
			return;
		}

		setLoading(true);
		setError("");
		setNotice("");
		try {
			const uploadResult = await callbackFun(
				uploadableRows.map((row) => ({ i_code: row.i_code, ginesys_code: row.ginesys_code })),
			);
			if (!uploadResult || uploadResult.status === "error" || uploadResult.success === false) {
				setError(uploadResult?.message || "Upload failed.");
				return;
			}

			const serverMissing = new Set((uploadResult?.result?.missing_i_codes || []).map(normalizeKeyValue));
			const remainingMissing = previewRows.filter(
				(row) => row.__status === "Missing" || serverMissing.has(normalizeKeyValue(row.i_code)),
			);
			if (remainingMissing.length) {
				setPreviewRows(
					remainingMissing.map((row) => ({
						...row,
						__isValid: false,
						__status: "Missing",
						__note: "RR code not found",
					})),
				);
				setNotice(
					`${remainingMissing.length} RR code${remainingMissing.length === 1 ? "" : "s"} not found and skipped.`,
				);
				return;
			}

			handleClose();
		} finally {
			setLoading(false);
		}
	};

	const uploadPreviewColumns = useMemo(
		() => [
			{
				key: "__upload_status",
				name: "UPLOAD STATUS",
				headerAlign: "center",
				align: "center",
				minWidth: 160,
				maxWidth: 180,
				valueGetter: ({ row }) => row.__status || "Valid",
				renderCell: ({ row }) => (
					<Box color={statusColor(row)} fontWeight={600}>
						{row.__status || "Valid"}
					</Box>
				),
			},
			{
				key: "i_code",
				name: "RR CODE",
				headerAlign: "center",
				align: "center",
				minWidth: 150,
			},
			{
				key: "ginesys_code",
				name: "GINESYS CODE",
				headerAlign: "center",
				align: "center",
				minWidth: 160,
			},
			{
				key: "__note",
				name: "NOTE",
				headerAlign: "center",
				align: "center",
				minWidth: 280,
				valueGetter: ({ row }) => row.__note || "",
				renderCell: ({ row }) => (
					<Typography variant="body2" color={statusColor(row)} sx={{ width: "100%" }} textAlign="center">
						{row.__note || ""}
					</Typography>
				),
			},
			{
				key: "__delete",
				name: "DELETE",
				headerAlign: "center",
				align: "center",
				minWidth: 90,
				maxWidth: 90,
				valueGetter: () => "",
				renderCell: ({ row }) => (
					<IconButton
						size="small"
						color="error"
						onClick={(event) => {
							event.stopPropagation();
							handleDeleteRow(row.__rowId);
						}}>
						<DeleteOutlineRounded fontSize="small" />
					</IconButton>
				),
			},
		],
		[],
	);

	const previewGridKey = useMemo(
		() => previewRows.map((row) => `${row.__rowId}:${row.__status}:${row.__note}:${row.__isValid}`).join("||"),
		[previewRows],
	);

	return (
		<Dialog maxWidth="xl" fullWidth scroll="paper" open={modal} onClose={handleClose} PaperProps={{ sx: { height: "82vh" } }}>
			<DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
				<Typography variant="h6">Upload Ginesys Code</Typography>
				<IconButton onClick={handleClose}>
					<ClearRounded />
				</IconButton>
			</DialogTitle>
			<DialogContent sx={{ display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden", pt: 1.5, pb: 1.5 }}>
				<Stack spacing={1.25} sx={{ minHeight: 0, flex: 1, overflow: "hidden" }}>
					{previewRows.length === 0 && (
						<Alert severity="info" sx={{ py: 0.5, alignItems: "center" }}>
							Map <strong>RR Code</strong> and <strong>Ginesys Code</strong>. A row that already has a Ginesys code is
							marked <strong>Will replace</strong> and the uploaded value overwrites it. Unknown RR codes are skipped.
						</Alert>
					)}

					<Box>
						<Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
							<Button
								component="label"
								variant="contained"
								color="primary"
								disableElevation
								size="small"
								sx={{ minWidth: 150, textTransform: "none", fontWeight: 600, borderRadius: 2, py: 0.9 }}
								startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <UploadRounded />}>
								Choose CSV / Excel
								<input hidden type="file" accept=".csv,.xlsx" onChange={handleFileChange} disabled={loading} />
							</Button>
							<Button
								variant="outlined"
								color="secondary"
								onClick={handleDownloadFormat}
								disabled={loading}
								size="small"
								startIcon={<FileDownloadRounded />}
								sx={{
									minWidth: 138,
									textTransform: "none",
									fontWeight: 600,
									borderRadius: 2,
									borderStyle: "dashed",
									py: 0.9,
								}}>
								Upload Format
							</Button>
							<Button
								variant="contained"
								color="success"
								disableElevation
								size="small"
								startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshRounded />}
								onClick={handlePreview}
								disabled={loading || !headers.length || rrColumnIndex === "" || ginesysColumnIndex === ""}
								sx={{ minWidth: 132, textTransform: "none", fontWeight: 600, borderRadius: 2, py: 0.9 }}>
								Preview Rows
							</Button>
						</Stack>
						{fileName && (
							<Typography variant="body2" color="text.secondary" pt={1}>
								{`${fileName} - ${sourceRows.length} rows found`}
							</Typography>
						)}
					</Box>

					{headers.length > 0 && (
						<Box sx={{ display: "flex", alignItems: "center", columnGap: 2, pt: 1 }}>
							<FormControl size="small" sx={{ minWidth: 220 }}>
								<InputLabel id="upload-ginesys-rr-column-label">RR Code Column</InputLabel>
								<Select
									labelId="upload-ginesys-rr-column-label"
									label="RR Code Column"
									value={rrColumnIndex}
									onChange={(event) => setRrColumnIndex(event.target.value)}>
									{headers.map((header, index) => (
										<MenuItem key={`${header}-${index}`} value={String(index)}>
											{header}
										</MenuItem>
									))}
								</Select>
							</FormControl>
							<FormControl size="small" sx={{ minWidth: 220 }}>
								<InputLabel id="upload-ginesys-code-column-label">Ginesys Code Column</InputLabel>
								<Select
									labelId="upload-ginesys-code-column-label"
									label="Ginesys Code Column"
									value={ginesysColumnIndex}
									onChange={(event) => setGinesysColumnIndex(event.target.value)}>
									{headers.map((header, index) => (
										<MenuItem key={`${header}-${index}`} value={String(index)}>
											{header}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Box>
					)}

					{error && <Alert severity="error">{error}</Alert>}
					{notice && <Alert severity="warning">{notice}</Alert>}

					<Stack sx={{ minHeight: 0, flex: 1, overflow: "hidden" }}>
						{previewRows.length > 0 && (
							<Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
								<TanstackDatagrid
									key={previewGridKey}
									columns={uploadPreviewColumns}
									pageData={previewRows}
									setPageData={setPreviewRows}
									getRowId={(row) => row.__rowId}
									disablePagination={true}
									serverSidePagination={false}
									enableHorizontalScroll={true}
									headerHeight={40}
									rowHeight={35}
									processRowUpdate={(newRow) => newRow}
									isCellEditable={() => false}
									children={
										<Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
											<Typography variant="body2" color="text.secondary">
												{`${uploadableRows.length} RR code${uploadableRows.length === 1 ? "" : "s"} can be uploaded from ${sourceRows.length} rows`}
											</Typography>
										</Box>
									}
								/>
							</Box>
						)}
					</Stack>
				</Stack>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button variant="outlined" onClick={handleClose} disabled={loading}>
					Cancel
				</Button>
				<Button variant="contained" onClick={handleFormSubmit} disabled={loading || uploadableRows.length === 0}>
					Save Upload
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default GinesysCodeUploadModal;
